import React, { useState, useEffect, useCallback } from 'react';
import economiesApi from '../services/economies';
import donneesApi from '../services/donnees';
import './Economies.css';

// ─── Économies réalisées — saisie manuelle ───────────────────────────────────
// Volontairement minimal : un fournisseur, une année, un montant annuel
// (+ commentaire libre). Le champ fournisseur SUGGÈRE les noms du grand livre
// (datalist), sans l'imposer — on peut saisir un fournisseur absent du livre.

const eur = (v) => Number(v).toLocaleString('fr-FR', { maximumFractionDigits: 0 }) + ' €';

export default function Economies({ isAdmin }) {
  const anneeCourante = new Date().getFullYear();
  const [lignes, setLignes] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [form, setForm] = useState({ fournisseur: '', annee: anneeCourante, montant: '', commentaire: '' });
  const [enregistre, setEnregistre] = useState(false);

  const charger = useCallback(() => {
    economiesApi.getAll()
      .then(r => setLignes(r.data.economies || []))
      .catch(e => alert(e.response?.data?.error || e.message));
  }, []);

  useEffect(() => { charger(); }, [charger]);

  // Suggestions depuis le grand livre — silencieux si l'entrepôt est absent.
  useEffect(() => {
    donneesApi.fgFournisseurs({})
      .then(r => setSuggestions((r.data.fournisseurs || []).map(f => f.fournisseur).filter(Boolean)))
      .catch(() => {});
  }, []);

  const ajouter = async (e) => {
    e.preventDefault();
    if (enregistre) return;
    setEnregistre(true);
    try {
      await economiesApi.create(form);
      setForm(f => ({ ...f, fournisseur: '', montant: '', commentaire: '' }));
      charger();
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    } finally {
      setEnregistre(false);
    }
  };

  const supprimer = async (l) => {
    if (!window.confirm(`Supprimer l'économie « ${l.fournisseur} · ${l.annee} · ${eur(l.montant)} » ?`)) return;
    try {
      await economiesApi.remove(l.id);
      charger();
    } catch (err) { alert(err.response?.data?.error || err.message); }
  };

  if (!lignes) return <div className="eco-vide">Chargement…</div>;

  const totaux = lignes.reduce((acc, l) => { acc[l.annee] = (acc[l.annee] || 0) + l.montant; return acc; }, {});
  const annees = Object.keys(totaux).sort((a, b) => b - a);

  return (
    <div className="eco-page">
      {annees.length > 0 && (
        <div className="eco-totaux">
          {annees.map(a => (
            <div key={a} className="eco-total">
              <span className="eco-total-annee">{a}</span>
              <span className="eco-total-montant">{eur(totaux[a])}</span>
            </div>
          ))}
        </div>
      )}

      {isAdmin && (
        <form className="eco-form" onSubmit={ajouter}>
          <input
            list="eco-fournisseurs"
            placeholder="Fournisseur"
            value={form.fournisseur}
            onChange={e => setForm(f => ({ ...f, fournisseur: e.target.value }))}
            required
          />
          <datalist id="eco-fournisseurs">
            {suggestions.map(s => <option key={s} value={s} />)}
          </datalist>
          <input
            type="number" className="eco-annee" min="2000" max="2100"
            value={form.annee}
            onChange={e => setForm(f => ({ ...f, annee: e.target.value }))}
            required
          />
          <input
            type="number" className="eco-montant" step="0.01" placeholder="Montant annuel €"
            value={form.montant}
            onChange={e => setForm(f => ({ ...f, montant: e.target.value }))}
            required
          />
          <input
            className="eco-commentaire" placeholder="Commentaire (optionnel)"
            value={form.commentaire}
            onChange={e => setForm(f => ({ ...f, commentaire: e.target.value }))}
          />
          <button type="submit" disabled={enregistre}>{enregistre ? '…' : '+ Ajouter'}</button>
        </form>
      )}

      <table className="eco-table">
        <thead>
          <tr>
            <th>Fournisseur</th><th>Année</th><th className="eco-num">Montant annuel</th>
            <th>Commentaire</th><th>Saisi par</th>{isAdmin && <th />}
          </tr>
        </thead>
        <tbody>
          {lignes.map(l => (
            <tr key={l.id}>
              <td><b>{l.fournisseur}</b></td>
              <td>{l.annee}</td>
              <td className="eco-num"><b>{eur(l.montant)}</b></td>
              <td className="eco-com">{l.commentaire || '—'}</td>
              <td className="eco-qui">{l.cree_par_nom || '—'}</td>
              {isAdmin && (
                <td className="eco-actions">
                  <button title="Supprimer" onClick={() => supprimer(l)}>🗑️</button>
                </td>
              )}
            </tr>
          ))}
          {lignes.length === 0 && (
            <tr><td colSpan={isAdmin ? 6 : 5} className="eco-vide">
              Aucune économie saisie pour l'instant{isAdmin ? ' — ajoutez la première ci-dessus.' : '.'}
            </td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
